import React from 'react'
import { WithTooltip } from '../../design-system'
import { BackendSystemItemTooltipContent } from './backend-system-item-tooltip-content'
import { css, cx } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import {
  PlugIcon,
  WebServiceIcon,
  CsDatabaseIcon
} from '../../legacy-design-system/icons'
import { paragraphTextCss } from '../../legacy-design-system/text'
import { useIntl } from 'react-intl'

export const containerWidth = 120
export const containerHeight = 82
export const itemSize = 65
const iconSize = 35

export type BackendSystemType = 'db' | 'ws' | 'ldap'

type BackendSystemIcons = Record<BackendSystemType, React.ReactNode>

const backendSystemIcons: BackendSystemIcons = {
  db: <CsDatabaseIcon color={theme.color.neutral60} size={iconSize} />,
  ws: <WebServiceIcon color={theme.color.neutral60} size={iconSize} />,
  ldap: <PlugIcon color={theme.color.neutral60} size={iconSize} />
}

interface BackendSystemItemProps {
  systemType: BackendSystemType
  location: string
  port: string
  marginTop: number
  vendorName?: string
  isSelected?: boolean
  onClick?: () => void
}

export function BackendSystemItem({
  systemType,
  location,
  port,
  marginTop,
  vendorName,
  isSelected = false,
  onClick
}: BackendSystemItemProps) {
  const url = buildUrl({
    location,
    port,
    noPortDetectedMessage: useIntl().formatMessage({
      id: 'detail_applicationFlowMap_noPortDetected'
    })
  })

  return (
    <div
      data-e2e="backend-system-item"
      onClick={onClick}
      className={css`
        height: ${containerHeight}px;
        width: ${containerWidth}px;
        margin-top: ${marginTop}px;
      `}
    >
      <div
        className={css`
          flex: 1;
          justify-content: center;
          display: flex;
        `}
      >
        <WithTooltip
          placement={'top'}
          tooltipMaxWidth="300px"
          renderTooltipContent={() => {
            return (
              <BackendSystemItemTooltipContent
                systemType={systemType}
                location={url}
                vendorName={vendorName}
              />
            )
          }}
          children={() => (
            <div
              className={css`
                display: flex;

                cursor: pointer;

                height: ${itemSize}px;
                width: ${itemSize}px;

                justify-content: center;
                align-items: center;

                border-width: ${isSelected ? '2.5px' : '0.75px'};
                border-style: solid;
                border-color: ${isSelected
                  ? theme.color.purple70
                  : theme.color.neutral50};

                background-color: ${theme.color.white};
                border-radius: 50%;
              `}
            >
              {systemType === 'db' ? (
                <div
                  className={css`
                    margin-left: 10px;
                  `}
                >
                  {backendSystemIcons[systemType]}
                </div>
              ) : (
                backendSystemIcons[systemType]
              )}
            </div>
          )}
        />
      </div>
      <div
        className={cx(
          paragraphTextCss,
          css`
            position: relative;
            bottom: 18px;
            text-overflow: ellipsis;
            overflow: hidden;
            text-align: center;
            white-space: nowrap;
            cursor: pointer;
            padding-top: 18px;
          `
        )}
      >
        {url}
      </div>
    </div>
  )
}

function buildUrl({
  location,
  port,
  noPortDetectedMessage
}: {
  location: string
  port: string
  noPortDetectedMessage: string
}) {
  const isPortDetected = port !== '' && Number(port) > 0

  return isPortDetected
    ? `${location}:${port}`
    : `${location}: [${noPortDetectedMessage}]`
}
